/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AttomDataType,
  AttributeType,
  LayoutImageType,
  LiveChatDataType,
  LocationDataType,
  LocationType,
  ProjectAttachmentType,
  ProjectDetailsType,
  ProjectSettingCodeType,
  WeatherDataType
} from 'data/types';
import { clearInapplicableWindowsAnswer } from 'features/project/clear-inapplicable-windows-answer';

export interface ProjectState {
  instanceUniqueId: string | null;
  projectSettings: AttributeType[];
  // Monotonic project-settings save-completion nonce (AHA-6796): bumped by
  // the autosave watcher only AFTER a successful projectSettings
  // saveInstanceData, so consumers (the confidence-score refetch) can key on
  // saved state rather than in-flight edits — the settings mirror of
  // structure-slice's equipmentSaveVersion.
  settingsSaveVersion: number;
  attomData: AttomDataType | null;
  addressSearched: boolean;
  addressId: number | null;
  propertyLocation: LocationType | null;
  propertySummary: string | null;
  totalBTUs: { heating: number; cooling: number };
  totalLatentGain: number;
  locationData: LocationDataType | null;
  // True while the ATTOM-driven getLocationData + insulation-seed chain in
  // utils/property.ts is in flight. Creation paths (autosave-watcher's lazy
  // creation, utils/data createNewStructure) check it to defer their
  // fallback-'4' insulation seed to that chain instead of persisting a
  // possibly-wrong climate zone.
  isLocationDataPending: boolean;
  weatherData: WeatherDataType | null;
  projectDetails: ProjectDetailsType | null;
  projectAttachments: {
    images: ProjectAttachmentType[];
    files: ProjectAttachmentType[];
  };
  liveChatData: LiveChatDataType | null;
  liveChatSummary: string | null;
  goalStatement: string | null;
  layoutImageIds: number[];
  layoutImages: LayoutImageType[];
  customerAddresses: string[];
  suggestedStructureTypeId: number | null;
  suggestedStructureSubtypeId: number | null;
  suggestedStructureName: string | null;
}

export const defaultProjectState: ProjectState = {
  instanceUniqueId: null,
  projectSettings: [],
  settingsSaveVersion: 0,
  attomData: null,
  addressSearched: false,
  addressId: null,
  propertyLocation: null,
  propertySummary: null,
  totalBTUs: { heating: 0, cooling: 0 },
  totalLatentGain: 0,
  locationData: null,
  isLocationDataPending: false,
  weatherData: null,
  projectDetails: null,
  projectAttachments: { images: [], files: [] },
  liveChatData: null,
  liveChatSummary: null,
  goalStatement: null,
  layoutImageIds: [],
  layoutImages: [],
  customerAddresses: [],
  suggestedStructureTypeId: null,
  suggestedStructureSubtypeId: null,
  suggestedStructureName: null
};

export const projectSlice = createSlice({
  name: 'project',
  initialState: defaultProjectState,
  reducers: {
    setInstanceUniqueId: (state, action: PayloadAction<string | null>) => {
      state.instanceUniqueId = action.payload;
    },
    setProjectSettings: (state, action: PayloadAction<AttributeType[]>) => {
      state.projectSettings = action.payload;
    },
    updateProjectSetting: (
      state,
      action: PayloadAction<
        | { attributeId: number; value: any }
        | { attributeCode: ProjectSettingCodeType; value: any }
        | {
            attributeCode: ProjectSettingCodeType;
            value: any;
            attributeDefinition?: AttributeType;
          }
      >
    ) => {
      const { payload } = action;
      const index = state.projectSettings.findIndex((setting) =>
        'attributeId' in payload
          ? setting.id === payload.attributeId
          : setting.code === payload.attributeCode
      );
      let code: string | null = null;
      if (index !== -1) {
        state.projectSettings[index].value = payload.value;
        code = state.projectSettings[index].code;
      } else if ('attributeCode' in payload) {
        code = payload.attributeCode;
        if ('attributeDefinition' in payload && payload.attributeDefinition) {
          state.projectSettings.push({
            ...payload.attributeDefinition,
            value: payload.value
          });
        } else {
          state.projectSettings.push({
            id: -1,
            code: payload.attributeCode,
            value: payload.value,
            type: 'select',
            label: 'Structure Orientation',
            priority: 0,
            inheritable: false
          } as AttributeType);
        }
      }
      if (code === 'year_built') {
        clearInapplicableWindowsAnswer(state.projectSettings, payload.value);
      }
    },
    setProjectSettingValue: {
      reducer: (
        state,
        action: PayloadAction<{ code: ProjectSettingCodeType; value: any }>
      ) => {
        const index = state.projectSettings.findIndex(
          (setting) => setting.code === action.payload.code
        );
        if (index !== -1) {
          state.projectSettings[index].value = action.payload.value;
          if (action.payload.code === 'year_built') {
            clearInapplicableWindowsAnswer(
              state.projectSettings,
              action.payload.value
            );
          }
        }
      },
      prepare: (code: ProjectSettingCodeType, value: any) => ({
        payload: { code, value }
      })
    },
    // See settingsSaveVersion in ProjectState: dispatched only by the
    // autosave watcher after a projectSettings save succeeds. Monotonic — a
    // stale-but-larger nonce only changes a refetch key, never its meaning.
    bumpSettingsSaveVersion: (state) => {
      state.settingsSaveVersion += 1;
    },
    setAttomData: (state, action: PayloadAction<AttomDataType | null>) => {
      state.attomData = action.payload;
    },
    setAddressSearched: (state, action: PayloadAction<boolean>) => {
      state.addressSearched = action.payload;
    },
    setAddressId: (state, action: PayloadAction<number | null>) => {
      state.addressId = action.payload;
    },
    setPropertyLocation: (
      state,
      action: PayloadAction<LocationType | null>
    ) => {
      state.propertyLocation = action.payload;
    },
    setPropertySummary: (state, action: PayloadAction<string | null>) => {
      state.propertySummary = action.payload;
    },
    setTotalBTUs: (
      state,
      action: PayloadAction<{ heating: number; cooling: number }>
    ) => {
      state.totalBTUs = action.payload;
    },
    setTotalLatentGain: (state, action: PayloadAction<number>) => {
      state.totalLatentGain = action.payload;
    },
    setLocationData: (
      state,
      action: PayloadAction<LocationDataType | null>
    ) => {
      state.locationData = action.payload;
    },
    setIsLocationDataPending: (state, action: PayloadAction<boolean>) => {
      state.isLocationDataPending = action.payload;
    },
    setWeatherData: (state, action: PayloadAction<WeatherDataType | null>) => {
      state.weatherData = action.payload;
    },
    setProjectDetails: (
      state,
      action: PayloadAction<ProjectDetailsType | null>
    ) => {
      state.projectDetails = action.payload;
    },
    setProjectAttachments: (
      state,
      action: PayloadAction<{
        images: ProjectAttachmentType[];
        files: ProjectAttachmentType[];
      }>
    ) => {
      state.projectAttachments = action.payload;
    },
    setLiveChatData: (
      state,
      action: PayloadAction<LiveChatDataType | null>
    ) => {
      state.liveChatData = action.payload;
    },
    setLiveChatSummary: (state, action: PayloadAction<string | null>) => {
      state.liveChatSummary = action.payload;
    },
    setGoalStatement: (state, action: PayloadAction<string | null>) => {
      state.goalStatement = action.payload;
    },
    setLayoutImageIds: (state, action: PayloadAction<number[]>) => {
      state.layoutImageIds = action.payload;
    },
    setLayoutImages: (state, action: PayloadAction<LayoutImageType[]>) => {
      state.layoutImages = action.payload;
    },
    setCustomerAddresses: (state, action: PayloadAction<string[]>) => {
      state.customerAddresses = action.payload;
    },
    setSuggestedStructureType: (
      state,
      action: PayloadAction<{
        typeId: number | null;
        subtypeId: number | null;
        name: string | null;
      }>
    ) => {
      state.suggestedStructureTypeId = action.payload.typeId;
      state.suggestedStructureSubtypeId = action.payload.subtypeId;
      state.suggestedStructureName = action.payload.name;
    }
  }
});

export const {
  setInstanceUniqueId,
  setProjectSettings,
  updateProjectSetting,
  setProjectSettingValue,
  bumpSettingsSaveVersion,
  setAttomData,
  setAddressSearched,
  setAddressId,
  setPropertyLocation,
  setPropertySummary,
  setTotalBTUs,
  setTotalLatentGain,
  setLocationData,
  setIsLocationDataPending,
  setWeatherData,
  setProjectDetails,
  setProjectAttachments,
  setLiveChatData,
  setLiveChatSummary,
  setGoalStatement,
  setLayoutImageIds,
  setLayoutImages,
  setCustomerAddresses,
  setSuggestedStructureType
} = projectSlice.actions;

export default projectSlice.reducer;
